import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PublicShell } from "@/pages/PublicSalon";
import { getPublicCardStyle } from "@/lib/contrast";
import { Calendar, LogOut, RotateCcw, Gift, Coins, Copy, Check } from "lucide-react";

const STATUS_LABEL = {
  in_progress: "In Bearbeitung",
  confirmed: "Bestätigt",
  alternative_proposed: "Alternativ vorgeschlagen",
  cancelled: "Storniert",
  completed: "Abgeschlossen",
};

const REWARD_TYPE_LABEL = {
  discount_percent: "% Rabatt",
  discount_fixed: "€ Rabatt",
  free_service: "Gratis-Leistung",
};

const CUST_TOKEN_KEY = (slug) => `studiomv_customer_${slug}`;

export default function CustomerPortal() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [salonData, setSalonData] = useState(null);
  const [view, setView] = useState("login"); // login | register | account
  const [tab, setTab] = useState("bookings"); // bookings | loyalty | profile
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loyalty, setLoyalty] = useState(null); // {settings, balance, rewards, transactions, redemptions}
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", birthday: "" });
  const [profileForm, setProfileForm] = useState({ name: "", phone: "", birthday: "" });
  const [profileMsg, setProfileMsg] = useState("");
  const [err, setErr] = useState("");
  const [redeemMsg, setRedeemMsg] = useState("");
  const [copied, setCopied] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");

  useEffect(() => {
    api.get(`/public/salon/${slug}`).then(({ data }) => setSalonData(data));
    const tok = localStorage.getItem(CUST_TOKEN_KEY(slug));
    if (tok) {
      api.get(`/public/salon/${slug}/customer/me`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(({ data }) => {
          setCustomer(data); setView("account");
          setProfileForm({ name: data.name || "", phone: data.phone || "", birthday: data.birthday || "" });
          loadAccountData(tok);
        })
        .catch(() => localStorage.removeItem(CUST_TOKEN_KEY(slug)));
    }
  }, [slug]);

  const authHeader = (tok) => ({ Authorization: `Bearer ${tok || localStorage.getItem(CUST_TOKEN_KEY(slug))}` });

  const loadAccountData = async (tok) => {
    const headers = { Authorization: `Bearer ${tok}` };
    const [b, l] = await Promise.all([
      api.get(`/public/salon/${slug}/customer/bookings`, { headers }),
      api.get(`/public/salon/${slug}/customer/loyalty`, { headers }).catch(() => ({ data: null })),
    ]);
    setBookings(b.data);
    setLoyalty(l.data);
  };

  const login = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post(`/public/salon/${slug}/customer/login`, { salon_slug: slug, email: form.email, password: form.password });
      localStorage.setItem(CUST_TOKEN_KEY(slug), data.token);
      setCustomer(data.customer); setView("account");
      setProfileForm({ name: data.customer.name || "", phone: data.customer.phone || "", birthday: data.customer.birthday || "" });
      loadAccountData(data.token);
    } catch (e2) {
      const det = e2.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "verification_required") {
        setPendingEmail(det.email || form.email);
        setView("verify_pending");
        setErr("");
        return;
      }
      setErr(typeof det === "string" ? det : "Fehler");
    }
  };

  const register = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = { salon_slug: slug, email: form.email, password: form.password, name: form.name, phone: form.phone };
      if (form.birthday) payload.birthday = form.birthday;
      const { data } = await api.post(`/public/salon/${slug}/customer/register`, payload);
      if (data.verification_required) {
        // Double-Opt-In: no token returned — show pending screen
        setView("verify_pending");
        setPendingEmail(form.email);
        return;
      }
      // Legacy flow (shouldn't happen): token returned
      if (data.token) {
        localStorage.setItem(CUST_TOKEN_KEY(slug), data.token);
        setCustomer(data.customer); setView("account");
        setProfileForm({ name: data.customer.name || "", phone: data.customer.phone || "", birthday: data.customer.birthday || "" });
        loadAccountData(data.token);
      }
    } catch (e2) { setErr(e2.response?.data?.detail || "Fehler"); }
  };

  const resendVerification = async () => {
    setErr("");
    try {
      const { data } = await api.post(`/public/salon/${slug}/customer/resend-verification`, { email: pendingEmail || form.email });
      setProfileMsg(data.message || "Bestätigungs-Link erneut versendet.");
      setTimeout(() => setProfileMsg(""), 4000);
    } catch (e2) { setErr(e2.response?.data?.detail || "Fehler beim erneuten Versand"); }
  };

  const customerForgotPassword = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post(`/public/salon/${slug}/customer/forgot-password`, { email: form.email });
      setProfileMsg(data.message || "Reset-Link versendet.");
      setView("forgot_sent");
    } catch (e2) { setErr(e2.response?.data?.detail || "Fehler"); }
  };

  const saveProfile = async () => {
    setProfileMsg(""); setErr("");
    try {
      const { data } = await api.put(`/public/salon/${slug}/customer/me`, profileForm, { headers: authHeader() });
      setCustomer(data);
      setProfileForm({ name: data.name || "", phone: data.phone || "", birthday: data.birthday || "" });
      setProfileMsg("Profil aktualisiert");
      setTimeout(() => setProfileMsg(""), 4000);
    } catch (e2) { setErr(e2.response?.data?.detail || "Fehler beim Speichern"); }
  };

  const logout = () => {
    localStorage.removeItem(CUST_TOKEN_KEY(slug));
    setCustomer(null); setView("login");
  };

  const rebook = (b) => {
    navigate(`/salon/${slug}/booking?service=${b.service_id}${b.staff_id ? `&staff=${b.staff_id}` : ""}`);
  };

  const redeem = async (rewardId) => {
    setRedeemMsg(""); setErr("");
    try {
      const { data } = await api.post(`/public/salon/${slug}/customer/redeem/${rewardId}`, {}, { headers: authHeader() });
      setRedeemMsg(data.message);
      loadAccountData(localStorage.getItem(CUST_TOKEN_KEY(slug)));
    } catch (e2) { setErr(e2.response?.data?.detail || "Einlösung fehlgeschlagen"); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(""), 1500); } catch {}
  };

  if (!salonData) return <div className="min-h-screen flex items-center justify-center">Lade ...</div>;
  const { salon, theme, header, footer } = salonData;
  const bookingAllowed = (salonData.plan_features || []).includes("bookings");
  const heading = theme?.heading_font || "Playfair Display";
  const primary = theme?.primary_color || "#C87A5F";
  const loyaltyEnabled = loyalty?.settings?.enabled;
  // Shared card surface — keeps every form/card readable, even on dark themes.
  const card = getPublicCardStyle(theme);
  const cardStyle = { background: card.bg, color: card.text, borderColor: card.border };
  // Inputs sit on top of the card — match border + text colour so values stay readable
  // even when admins use a custom `text_color` that contrasts with the body but not
  // the input background.
  const inputStyle = card.bg === "#fff"
    ? { color: "#1a1a1a" }
    : { background: card.subtle, color: card.text, borderColor: card.border };

  return (
    <PublicShell salon={salon} theme={theme} header={header} footer={footer}>
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        {view === "account" && customer ? (
          <>
            <div className="flex justify-between items-start mb-10 flex-wrap gap-4">
              <div>
                <p className="mv-label mb-2">Mein Konto</p>
                <h1 style={{ fontFamily: `'${heading}', serif` }} className="text-4xl md:text-5xl">Hallo {customer.name}</h1>
                <p className="text-sm mt-2 opacity-75">{customer.email}</p>
              </div>
              <button data-testid="customer-logout" onClick={logout} className="mv-btn-ghost text-sm"><LogOut className="w-4 h-4" /> Abmelden</button>
            </div>

            <div className="flex border-b mb-6 flex-wrap" style={{ borderColor: "var(--mv-border)" }}>
              <button
                data-testid="portal-tab-bookings"
                onClick={() => setTab("bookings")}
                className="px-5 py-3 text-sm border-b-2"
                style={{
                  borderColor: tab === "bookings" ? primary : "transparent",
                  color: tab === "bookings" ? primary : "var(--mv-text-muted)",
                  fontWeight: tab === "bookings" ? 600 : 400,
                }}
              >
                Termine
              </button>
              {loyaltyEnabled && (
                <button
                  data-testid="portal-tab-loyalty"
                  onClick={() => setTab("loyalty")}
                  className="px-5 py-3 text-sm border-b-2 flex items-center gap-2"
                  style={{
                    borderColor: tab === "loyalty" ? primary : "transparent",
                    color: tab === "loyalty" ? primary : "var(--mv-text-muted)",
                    fontWeight: tab === "loyalty" ? 600 : 400,
                  }}
                >
                  <Gift className="w-4 h-4" /> Treuepunkte
                  <span className="ml-1 text-xs px-2 py-0.5 rounded-full" style={{ background: `${primary}22`, color: primary }}>
                    {loyalty.balance}
                  </span>
                </button>
              )}
              <button
                data-testid="portal-tab-profile"
                onClick={() => setTab("profile")}
                className="px-5 py-3 text-sm border-b-2"
                style={{
                  borderColor: tab === "profile" ? primary : "transparent",
                  color: tab === "profile" ? primary : "var(--mv-text-muted)",
                  fontWeight: tab === "profile" ? 600 : 400,
                }}
              >
                Profil
              </button>
            </div>

            {tab === "bookings" && (
              <>
                <h2 className="font-display text-2xl mb-4">Ihre Termine</h2>
                {bookings.length === 0 ? (
                  <div className="p-8 rounded-2xl text-center" style={{ background: "var(--mv-surface)" }}>
                    <Calendar className="w-8 h-8 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
                    <p className="text-sm opacity-75">Noch keine Termine vorhanden.</p>
                    {bookingAllowed && (
                      <Link to={`/salon/${slug}/booking`} className="mv-btn mt-4 inline-flex">Jetzt Termin buchen</Link>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bookings.map((b) => (
                      <div key={b.id} className="p-5 rounded-2xl border" style={cardStyle} data-testid={`cust-booking-${b.id}`}>
                        <div className="flex flex-wrap justify-between items-start gap-3">
                          <div>
                            <p className="font-medium">{b.service?.name || b.service_name}</p>
                            <p className="text-sm opacity-75">{new Date(b.date).toLocaleDateString("de-DE")} um {b.time} · {(b.service?.duration_minutes || 0) + (b.addon_duration || 0)} Min · {((b.service?.price || 0) + (b.addon_price || 0)).toFixed(2)} €</p>
                            {b.addons_data && b.addons_data.length > 0 && <p className="text-xs opacity-60 mt-1">+ {b.addons_data.map((a) => a.name).join(", ")}</p>}
                            {b.discount_amount > 0 && <p className="text-xs mt-1" style={{ color: primary }}>Treuebonus: −{b.discount_amount.toFixed(2)} € ({b.reward_name})</p>}
                          </div>
                          <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: `${primary}22`, color: primary }}>{STATUS_LABEL[b.status] || b.status}</span>
                        </div>
                        <button onClick={() => rebook(b)} className="mt-3 mv-btn-ghost text-xs"><RotateCcw className="w-3 h-3" /> Erneut buchen</button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {loyaltyEnabled && tab === "loyalty" && (
              <div className="space-y-8">
                <div className="p-6 md:p-8 rounded-2xl text-white" style={{ background: `linear-gradient(135deg, ${primary}, ${primary}dd)` }} data-testid="loyalty-balance-card">
                  <p className="text-sm opacity-90 mb-1">Ihr Punktestand</p>
                  <p className="text-5xl font-display flex items-end gap-2"><Coins className="w-10 h-10 mb-2" strokeWidth={1.5} /> {loyalty.balance}</p>
                  <p className="text-xs opacity-90 mt-3">
                    {loyalty.settings.points_per_euro} Punkt(e) pro € · Termine wirken ab Abschluss
                  </p>
                </div>

                {redeemMsg && (
                  <div className="p-4 rounded-xl text-sm" style={{ background: "var(--mv-surface)" }}>
                    <Check className="inline w-4 h-4 mr-1" style={{ color: "var(--mv-secondary)" }} /> {redeemMsg}
                  </div>
                )}
                {err && <p className="text-sm text-red-500">{err}</p>}

                <div>
                  <h2 className="font-display text-2xl mb-4">Belohnungen</h2>
                  {loyalty.rewards.length === 0 ? (
                    <p className="text-sm opacity-70">Aktuell keine Belohnungen verfügbar.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {loyalty.rewards.map((r) => {
                        const canRedeem = loyalty.balance >= r.points_cost;
                        return (
                          <div key={r.id} className="p-5 rounded-2xl border" style={cardStyle} data-testid={`reward-${r.id}`}>
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-medium">{r.name}</p>
                                <p className="text-xs opacity-70">
                                  {r.reward_type === "discount_percent" && `${r.reward_value}% Rabatt`}
                                  {r.reward_type === "discount_fixed" && `${r.reward_value}€ Rabatt`}
                                  {r.reward_type === "free_service" && "Gratis-Leistung"}
                                </p>
                              </div>
                              <span className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: `${primary}22`, color: primary }}>
                                <Coins className="inline w-3 h-3 mr-1" /> {r.points_cost}
                              </span>
                            </div>
                            {r.description && <p className="text-xs opacity-75 mb-3">{r.description}</p>}
                            <button
                              data-testid={`redeem-${r.id}`}
                              disabled={!canRedeem}
                              onClick={() => redeem(r.id)}
                              className="w-full py-2 rounded-full text-sm font-medium transition-colors"
                              style={{
                                background: canRedeem ? primary : "var(--mv-surface-2)",
                                color: canRedeem ? "#fff" : "var(--mv-text-muted)",
                                cursor: canRedeem ? "pointer" : "not-allowed",
                              }}
                            >
                              {canRedeem ? "Einlösen" : `Noch ${r.points_cost - loyalty.balance} Punkte fehlen`}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {loyalty.redemptions.filter((r) => r.status !== "used").length > 0 && (
                  <div>
                    <h2 className="font-display text-2xl mb-4">Ihre Codes</h2>
                    <div className="space-y-2">
                      {loyalty.redemptions.filter((r) => r.status !== "used").map((r) => (
                        <div key={r.id} className="flex flex-wrap justify-between items-center gap-3 p-4 rounded-xl border" style={cardStyle} data-testid={`my-code-${r.id}`}>
                          <div>
                            <p className="font-medium text-sm">{r.reward_name}</p>
                            <p className="text-xs opacity-70">Eingelöst am {new Date(r.created_at).toLocaleDateString("de-DE")}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono px-3 py-1 rounded-md" style={{ background: "var(--mv-surface)" }}>{r.code}</span>
                            <button onClick={() => copyCode(r.code)} className="mv-btn-ghost text-xs">
                              {copied === r.code ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              {copied === r.code ? "Kopiert" : "Kopieren"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs opacity-60 mt-3">Geben Sie den Code im Buchungsformular an, um den Rabatt auf den nächsten Termin anzuwenden.</p>
                  </div>
                )}

                {loyalty.transactions.length > 0 && (
                  <div>
                    <h2 className="font-display text-2xl mb-4">Verlauf</h2>
                    <div className="rounded-2xl border overflow-hidden" style={cardStyle}>
                      {loyalty.transactions.map((t, i) => (
                        <div key={t.id || i} className="flex justify-between items-center px-5 py-3 text-sm" style={{ borderTop: i ? "1px solid var(--mv-border)" : "none" }}>
                          <div>
                            <p>{t.reason}</p>
                            <p className="text-xs opacity-60">{new Date(t.created_at).toLocaleDateString("de-DE")}</p>
                          </div>
                          <span className="font-medium" style={{ color: t.amount >= 0 ? "var(--mv-secondary)" : "#b91c1c" }}>
                            {t.amount > 0 ? "+" : ""}{t.amount}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === "profile" && (
              <div className="max-w-xl space-y-4">
                <h2 className="font-display text-2xl mb-4">Profil</h2>
                <div className="p-6 rounded-2xl border space-y-4" style={cardStyle}>
                  <div>
                    <label className="block text-xs mb-1">Name</label>
                    <input className="mv-input" style={inputStyle} value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} data-testid="profile-name" />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Telefon</label>
                    <input className="mv-input" style={inputStyle} value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} data-testid="profile-phone" />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Geburtstag</label>
                    <input type="date" className="mv-input" style={inputStyle} value={profileForm.birthday || ""} onChange={(e) => setProfileForm({ ...profileForm, birthday: e.target.value })} data-testid="profile-birthday" />
                    {loyaltyEnabled && loyalty?.settings?.birthday_bonus > 0 && (
                      <p className="text-xs mt-1" style={{ color: primary }}>🎂 Erhalten Sie {loyalty.settings.birthday_bonus} Bonus-Punkte an Ihrem Geburtstag</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button onClick={saveProfile} className="px-5 py-2.5 rounded-full text-white font-medium" style={{ background: primary }} data-testid="profile-save">Speichern</button>
                    {profileMsg && <p className="text-sm" style={{ color: primary }}>{profileMsg}</p>}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : view === "forgot" ? (
          <div className="max-w-md mx-auto">
            <p className="mv-label mb-2 text-center">Passwort vergessen</p>
            <h1 style={{ fontFamily: `'${heading}', serif` }} className="text-3xl md:text-4xl text-center mb-6 leading-tight">Reset-Link anfordern</h1>
            <form onSubmit={customerForgotPassword} className="space-y-3 p-6 rounded-2xl border" style={cardStyle} data-testid="cust-forgot-form">
              <p className="text-sm opacity-75">Gib deine E-Mail ein — wir senden dir einen Link, um ein neues Passwort zu setzen.</p>
              <div><label className="block text-xs mb-1">E-Mail</label><input required type="email" className="mv-input" style={inputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-forgot-email" /></div>
              {err && <p className="text-sm text-red-500">{err}</p>}
              <button data-testid="cust-forgot-submit" className="w-full px-6 py-3 rounded-full text-white font-medium" style={{ background: primary }}>Reset-Link senden</button>
              <button type="button" onClick={() => { setView("login"); setErr(""); }} className="w-full text-xs opacity-70 hover:opacity-100">Zurück zum Login</button>
            </form>
          </div>
        ) : view === "forgot_sent" ? (
          <div className="max-w-md mx-auto text-center" data-testid="cust-forgot-sent">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center text-3xl" style={{ background: `${primary}15`, color: primary }}>&#9993;</div>
            <h1 style={{ fontFamily: `'${heading}', serif` }} className="text-3xl md:text-4xl mb-4 leading-tight">Mail unterwegs</h1>
            <p className="text-sm opacity-80 mb-6">{profileMsg || "Falls die E-Mail registriert ist, haben wir dir einen Reset-Link gesendet. Prüfe auch deinen Spam-Ordner."}</p>
            <button onClick={() => { setView("login"); setProfileMsg(""); }} className="px-6 py-2.5 rounded-full text-sm font-medium" style={{ background: primary, color: "#fff" }}>Zurück zum Login</button>
          </div>
        ) : view === "verify_pending" ? (
          <div className="max-w-md mx-auto text-center" data-testid="cust-verify-pending">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center text-3xl" style={{ background: `${primary}15`, color: primary }}>
              &#9993;
            </div>
            <p className="mv-label mb-2">E-Mail-Bestätigung</p>
            <h1 style={{ fontFamily: `'${heading}', serif` }} className="text-3xl md:text-4xl mb-4 leading-tight">Bitte bestätige deine E-Mail</h1>
            <p className="text-sm mb-6 opacity-80">
              Wir haben dir einen Bestätigungs-Link an
              {pendingEmail ? <> <strong className="break-all">{pendingEmail}</strong></> : " deine E-Mail-Adresse"} gesendet.
              Klicke auf den Link in der E-Mail, um dein Konto zu aktivieren.
            </p>
            {profileMsg && <p className="text-xs mb-4" style={{ color: primary }}>{profileMsg}</p>}
            {err && <p className="text-sm text-red-500 mb-3">{typeof err === "string" ? err : ""}</p>}
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button onClick={resendVerification} data-testid="cust-resend-verification" className="px-6 py-2.5 rounded-full text-sm font-medium border" style={{ borderColor: primary, color: primary }}>
                Bestätigungs-Link erneut senden
              </button>
              <button onClick={() => { setView("login"); setErr(""); setProfileMsg(""); }} className="px-6 py-2.5 rounded-full text-sm" data-testid="cust-back-to-login">
                Zurück zum Login
              </button>
            </div>
            <p className="text-xs mt-6 opacity-60">
              Keine E-Mail erhalten? Prüfe deinen Spam-Ordner.
            </p>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <p className="mv-label mb-2 text-center">Kundenbereich</p>
            <h1 style={{ fontFamily: `'${heading}', serif` }} className="text-4xl md:text-5xl text-center mb-8">{view === "login" ? "Anmelden" : "Konto erstellen"}</h1>

            <div className="flex p-1 rounded-full border mb-6 max-w-xs mx-auto" style={{ borderColor: "var(--mv-border)" }}>
              <button data-testid="cust-tab-login" onClick={() => setView("login")} className="flex-1 py-1.5 text-sm rounded-full" style={view === "login" ? { background: primary, color: "#fff" } : {}}>Anmelden</button>
              <button data-testid="cust-tab-register" onClick={() => setView("register")} className="flex-1 py-1.5 text-sm rounded-full" style={view === "register" ? { background: primary, color: "#fff" } : {}}>Registrieren</button>
            </div>

            <form onSubmit={view === "login" ? login : register} className="space-y-3 p-6 rounded-2xl border" style={cardStyle} data-testid="cust-auth-form">
              {view === "register" && (
                <>
                  <div><label className="block text-xs mb-1">Name</label><input required className="mv-input" style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
                  <div><label className="block text-xs mb-1">Telefon (optional)</label><input className="mv-input" style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><label className="block text-xs mb-1">Geburtstag (optional)</label><input type="date" className="mv-input" style={inputStyle} value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="cust-birthday" /></div>
                </>
              )}
              <div><label className="block text-xs mb-1">E-Mail</label><input required type="email" className="mv-input" style={inputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" /></div>
              <div><label className="block text-xs mb-1">Passwort</label><input required type="password" className="mv-input" style={inputStyle} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cust-password" /></div>
              {err && <p className="text-sm text-red-500">{err}</p>}
              <button data-testid="cust-submit" className="w-full px-6 py-3 rounded-full text-white font-medium" style={{ background: primary }}>{view === "login" ? "Anmelden" : "Konto erstellen"}</button>
              {view === "login" && (
                <button type="button" onClick={() => { setView("forgot"); setErr(""); }} data-testid="cust-forgot-link" className="w-full text-xs opacity-70 hover:opacity-100 underline">
                  Passwort vergessen?
                </button>
              )}
            </form>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
